/**
 * useUndoRedo Hook
 * Manages undo/redo history for DAW operations
 */

import { useState, useCallback, useRef } from 'react';

// Action types for tracking changes - comprehensive list for DAW operations
export const ActionTypes = {
  // Track operations
  ADD_TRACK: 'ADD_TRACK',
  REMOVE_TRACK: 'REMOVE_TRACK',
  UPDATE_TRACK: 'UPDATE_TRACK',
  REORDER_TRACKS: 'REORDER_TRACKS',
  
  // Project settings
  SET_BPM: 'SET_BPM',
  SET_MASTER_VOLUME: 'SET_MASTER_VOLUME',
  
  // Audio operations
  IMPORT_AUDIO: 'IMPORT_AUDIO',
  RECORD_AUDIO: 'RECORD_AUDIO',
  TRIM_AUDIO: 'TRIM_AUDIO',
  CUT_AUDIO: 'CUT_AUDIO',
  PASTE_AUDIO: 'PASTE_AUDIO',
  DELETE_AUDIO: 'DELETE_AUDIO',
  FADE_CHANGE: 'FADE_CHANGE',
  SPLIT_AUDIO_CLIP: 'SPLIT_AUDIO_CLIP',
  JOIN_AUDIO_CLIPS: 'JOIN_AUDIO_CLIPS',
  
  // Instrument track operations
  ADD_INSTRUMENT_TRACK: 'ADD_INSTRUMENT_TRACK',
  REMOVE_INSTRUMENT_TRACK: 'REMOVE_INSTRUMENT_TRACK',
  
  // MIDI operations
  RECORD_MIDI: 'RECORD_MIDI',
  ADD_MIDI_EVENT: 'ADD_MIDI_EVENT',
  REMOVE_MIDI_EVENT: 'REMOVE_MIDI_EVENT',
  UPDATE_MIDI_NOTES: 'UPDATE_MIDI_NOTES',
  
  // MIDI Clip operations
  ADD_MIDI_CLIP: 'ADD_MIDI_CLIP',
  REMOVE_MIDI_CLIP: 'REMOVE_MIDI_CLIP',
  UPDATE_MIDI_CLIP: 'UPDATE_MIDI_CLIP',
  MOVE_MIDI_CLIP: 'MOVE_MIDI_CLIP',
  RESIZE_MIDI_CLIP: 'RESIZE_MIDI_CLIP',
  DUPLICATE_MIDI_CLIP: 'DUPLICATE_MIDI_CLIP',
  SPLIT_MIDI_CLIP: 'SPLIT_MIDI_CLIP',
  JOIN_MIDI_CLIPS: 'JOIN_MIDI_CLIPS',
  
  // Piano Roll operations
  PIANO_ROLL_ADD_NOTE: 'PIANO_ROLL_ADD_NOTE',
  PIANO_ROLL_REMOVE_NOTE: 'PIANO_ROLL_REMOVE_NOTE',
  PIANO_ROLL_MOVE_NOTE: 'PIANO_ROLL_MOVE_NOTE',
  PIANO_ROLL_RESIZE_NOTE: 'PIANO_ROLL_RESIZE_NOTE',
  PIANO_ROLL_UPDATE_VELOCITY: 'PIANO_ROLL_UPDATE_VELOCITY',
  PIANO_ROLL_QUANTIZE: 'PIANO_ROLL_QUANTIZE',
  PIANO_ROLL_DELETE_NOTES: 'PIANO_ROLL_DELETE_NOTES',
  
  // Effect operations
  ADD_EFFECT: 'ADD_EFFECT',
  REMOVE_EFFECT: 'REMOVE_EFFECT',
  UPDATE_EFFECT: 'UPDATE_EFFECT',
  REORDER_EFFECTS: 'REORDER_EFFECTS',
  
  // Plugin operations
  ADD_PLUGIN: 'ADD_PLUGIN',
  REMOVE_PLUGIN: 'REMOVE_PLUGIN',
  UPDATE_PLUGIN: 'UPDATE_PLUGIN',
  REORDER_PLUGINS: 'REORDER_PLUGINS',
  
  // Automation operations
  ADD_AUTOMATION_LANE: 'ADD_AUTOMATION_LANE',
  REMOVE_AUTOMATION_LANE: 'REMOVE_AUTOMATION_LANE',
  UPDATE_AUTOMATION_LANE: 'UPDATE_AUTOMATION_LANE',
  ADD_AUTOMATION_POINT: 'ADD_AUTOMATION_POINT',
  MOVE_AUTOMATION_POINT: 'MOVE_AUTOMATION_POINT',
  DELETE_AUTOMATION_POINT: 'DELETE_AUTOMATION_POINT',
  
  // Chord operations
  ADD_CHORD_REGION: 'ADD_CHORD_REGION',
  REMOVE_CHORD_REGION: 'REMOVE_CHORD_REGION',
  UPDATE_CHORD_REGION: 'UPDATE_CHORD_REGION',
};

const MAX_HISTORY_SIZE = 100;

/**
 * Hook for managing undo/redo functionality
 */
export function useUndoRedo(initialState = {}) {
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  
  // Push an action to the undo stack
  const pushUndoAction = useCallback((action) => {
    undoStackRef.current.push(action);
    
    // Trim stack if too large
    if (undoStackRef.current.length > MAX_HISTORY_SIZE) {
      undoStackRef.current = undoStackRef.current.slice(-MAX_HISTORY_SIZE);
    }
    
    // Clear redo stack when new action is performed
    redoStackRef.current = [];
    
    setCanUndo(true);
    setCanRedo(false);
  }, []);
  
  // Perform undo operation
  const undo = useCallback((applyAction) => {
    if (undoStackRef.current.length === 0) return null;
    
    const action = undoStackRef.current.pop();
    redoStackRef.current.push(action);
    
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    
    // Apply the reverse action
    if (applyAction) {
      applyAction(action, 'undo');
    }
    
    return action;
  }, []);
  
  // Perform redo operation
  const redo = useCallback((applyAction) => {
    if (redoStackRef.current.length === 0) return null;
    
    const action = redoStackRef.current.pop();
    undoStackRef.current.push(action);
    
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
    
    // Apply the action
    if (applyAction) {
      applyAction(action, 'redo');
    }
    
    return action;
  }, []);
  
  // Clear all history
  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);
  
  // Get history stats
  const getHistoryStats = useCallback(() => ({
    undoCount: undoStackRef.current.length,
    redoCount: redoStackRef.current.length,
  }), []);
  
  return {
    canUndo,
    canRedo,
    pushUndoAction,
    undo,
    redo,
    clearHistory,
    getHistoryStats,
    ActionTypes,
  };
}

export default useUndoRedo;
